import React, { useState } from 'react'

import { 
    Box, 
    Typography, 
    IconButton, 
    Card, 
    CardContent, 
    Modal, 
    Button,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'

import PDFWebViewComponent from '../../../components/PDFWebViewComponent'

import { ContinueButton } from '../../../components/Buttons'

// Document Viewer Modal Component
const DocumentViewerModal = ({ open, onClose, fileUrl, fileName }) => {
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="document-viewer-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '90vw',
                    height: '90vh',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 24,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                    }}
                >
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                        {fileName || 'Document Viewer'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            padding: '4px',
                            '&:hover': { backgroundColor: '#E5E7EB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </Box>

                {/* Document Viewer */}
                <Box sx={{ width: '100%', height: 'calc(100% - 73px)' }}>
                    {fileUrl && (
                        <PDFWebViewComponent
                            initialFile={fileUrl}
                            hasLink={false}
                        />
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

// Confirmation Dialog Component
const ContinueWithoutTrainingSetsDialog = ({ open, onClose, onConfirm }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '16px',
                    maxWidth: '440px',
                }
            }}
        >
            <DialogContent sx={{ padding: '24px 24px 32px 24px', textAlign: 'center' }}>
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#111827',
                        mb: 2,
                        lineHeight: 1.4,
                    }}
                >
                    Continue without adding training sets?
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#6B7280',
                        lineHeight: 1.5,
                        mb: 0,
                    }}
                >
                    You can add them to the template later, or when generating documents
                </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 24px 24px', gap: 2, justifyContent: 'center' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                        backgroundColor: 'transparent',
                        border: '1px solid #D1D5DB',
                        minWidth: '100px',
                        '&:hover': {
                            backgroundColor: '#F9FAFB',
                            borderColor: '#9CA3AF',
                        },
                        textTransform: 'none',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    sx={{
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#FFFFFF',
                        backgroundColor: '#1E40AF',
                        border: 'none',
                        minWidth: '120px',
                        '&:hover': {
                            backgroundColor: '#1E3A8A',
                        },
                        textTransform: 'none',
                    }}
                >
                    Yes, continue
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// Training Set Card Component
const TrainingSetCard = ({ 
    trainingSet, 
    index, 
    onDelete, 
    onViewFile, 
    onReplaceFile 
}) => {
    const formatFileName = (fileName) => {
        if (!fileName) return ''
        return fileName.split('/').pop() || fileName
    }

    // const inputFileCount = trainingSet.inputFiles?.length || 0
    // const inputLabel = inputFileCount === 1 ? 'Input file' : 'Input files'

    return (
        <Card sx={{ 
            backgroundColor: '#FFFFFF', 
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            borderRadius: '8px',
            mb: 3
        }}>
            <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Two Column Layout for Files */}
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        {/* Input Files Column */}
                        {/* <Box sx={{ flex: 1 }}>
                            <Typography sx={{ 
                                fontSize: '14px', 
                                fontWeight: 500, 
                                color: '#374151', 
                                mb: 2 
                            }}>
                                {inputLabel} · {inputFileCount}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {trainingSet.inputFiles?.map((file, fileIndex) => (
                                    <Box
                                        key={fileIndex}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '8px 12px',
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '6px',
                                            border: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                        <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                            {formatFileName(file.name || file)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                onClick={() => onViewFile(file)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    backgroundColor: 'transparent',
                                                    color: '#4F46E5',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    backgroundColor: 'transparent',
                                                    color: '#4F46E5',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                Replace
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box> */}

                        {/* Output File Column */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ 
                                fontSize: '14px', 
                                fontWeight: 500, 
                                color: '#374151', 
                                mb: 2 
                            }}>
                                Example File {index + 1}
                            </Typography>
                            {trainingSet.outputFile ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        padding: '8px 12px',
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: '6px',
                                        border: '1px solid #E5E7EB',
                                    }}
                                >
                                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                    <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                        {formatFileName(trainingSet.outputFile.name || trainingSet.outputFile)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            onClick={() => onViewFile(trainingSet.outputFile)}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                backgroundColor: 'transparent',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: '#F3F4F6',
                                                },
                                            }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            onClick={() => onReplaceFile(trainingSet.outputFile, index)}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                backgroundColor: 'transparent',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: '#F3F4F6',
                                                },
                                            }}
                                        >
                                            Replace
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
                                    No output file
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Delete Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                            onClick={() => onDelete(trainingSet.id)}
                            startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                color: '#DC2626',
                                fontSize: '12px',
                                fontWeight: 500,
                                textTransform: 'none',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #F3F4F6',
                                backgroundColor: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#FEF2F2',
                                    borderColor: '#FECACA',
                                },
                            }}
                        >
                            Remove Output File
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}

export default function TrainingSetCreationComponent({
    onNextClick1,
    onBackClick,
    trainingSetFiles,
    setTrainingSetFiles,
    onAddTrainingSetClick,
    onDiscardTrainingSetClick,
    onInputFileSelect,
    onOutputFileSelect,
    onReplaceFile,
    inputFileList,
    outputFileList,
    removeInputFile,
    removeOutputFile,
    loading,
    setLoading,
    orgId,
    integrations,
}) {
    const navigate = useNavigate()
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

    const handleViewFile = (file) => {
        setSelectedDocument(file)
        setDocumentViewerOpen(true)
    }

    const handleReplaceFile = (file, trainingSetIndex) => {
        // Call the parent component's output file select handler with the file and index
        if (onReplaceFile) {
            onReplaceFile(file, trainingSetIndex)
        }
    }

    const handleCloseViewer = () => {
        setDocumentViewerOpen(false)
        setSelectedDocument(null)
    }

    const handleDeleteTrainingSet = (setId) => {
        const newTrainingSetFiles = trainingSetFiles.filter(
            (file) => file.id !== setId
        )
        setTrainingSetFiles(newTrainingSetFiles)
    }

    const handleContinueClick = () => {

        if (trainingSetFiles.length === 0) {
            setConfirmDialogOpen(true)
        } else {
            onNextClick1()
        }
    }

    const handleConfirmContinue = () => {
        setConfirmDialogOpen(false)
        onNextClick1()
    }

    const handleCancelConfirm = () => {
        setConfirmDialogOpen(false)
    }

    const formatFileName = (fileName) => {
        if (!fileName) return ''
        return fileName.split('/').pop() || fileName
    }
    
    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative' 
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 122px)',
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                },
                // Hide scrollbar while maintaining scroll functionality
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Opera
                }
            }}>
                <Box sx={{ mb: 4 }}>
                    <IconButton
                        onClick={onBackClick}
                        sx={{
                            padding: '6px',
                            marginLeft: '-8px',
                            marginTop: '-8px',
                            '&:hover': {
                                backgroundColor: '#F3F4F6',
                            },
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '20px', color: '#374151' }} />
                    </IconButton>
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#111827',
                                mb: 1
                            }}
                        >
                            Add Example File(s)
                        </Typography>
                        <Typography 
                            sx={{ 
                                fontSize: '14px',
                                color: '#6B7280',
                            }}
                        >
                            Easily incorporate company-specific terminology, formatting, and structure into AI-generated drafts.{' '}
                            {/* <Box component="span" sx={{ color: '#4F46E5', fontWeight: 500 }}>
                                Learn more
                            </Box> */}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 3, 
                    width: '100%',
                    maxWidth: '900px',
                    '@media (max-width: 768px)': {
                        maxWidth: '100%',
                    },
                    mb: '80px' 
                }}>
                    {/* Upload Areas Card */}
                    <Card sx={{ 
                        backgroundColor: '#FFFFFF', 
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                        borderRadius: '8px'
                    }}>
                        <CardContent sx={{ padding: '24px' }}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                {/* Input Files Upload */}
                                {/* <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#374151', 
                                        mb: 1 
                                    }}>
                                        Input file(s) · Min 1
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '2px dashed #D1D5DB',
                                            borderRadius: '8px',
                                            padding: '32px',
                                            textAlign: 'center',
                                            backgroundColor: '#FAFAFA',
                                            minHeight: '160px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                                        <Button
                                            onClick={onInputFileSelect}
                                            sx={{
                                                color: '#4F46E5',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                textDecoration: 'underline',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            Upload a file
                                        </Button>
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 1 }}>
                                            or drag and drop
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                                            PDF, DOCX, XLSX, CSV, or RTF up to (10MB)
                                        </Typography>
                                    </Box>

                                    {/* Selected Input Files */}
                                    {/*{inputFileList.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            {inputFileList.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        padding: '8px 12px',
                                                        backgroundColor: '#F9FAFB',
                                                        borderRadius: '6px',
                                                        border: '1px solid #E5E7EB',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                                    <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                                        {formatFileName(file.name || file)}
                                                    </Typography>
                                                    <IconButton
                                                        onClick={() => removeInputFile(index)}
                                                        sx={{ padding: '2px' }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box> */}

                                {/* Output File Upload */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ 
                                        fontSize: '14px', 
                                        fontWeight: 500, 
                                        color: '#374151', 
                                        mb: 1 
                                    }}>
                                        
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '2px dashed #D1D5DB',
                                            borderRadius: '8px',
                                            padding: '32px',
                                            textAlign: 'center',
                                            backgroundColor: '#FAFAFA',
                                            minHeight: '160px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                                        <Button
                                            onClick={onOutputFileSelect}
                                            sx={{
                                                color: '#4F46E5',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                textDecoration: 'underline',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            Upload a file
                                        </Button>
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 1 }}>
                                            or drag and drop
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                                            PDF, or DOCX up to (10MB)
                                        </Typography>
                                    </Box>

                                    {/* Selected Output File */}
                                    {outputFileList.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            {outputFileList.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        padding: '8px 12px',
                                                        backgroundColor: '#F9FAFB',
                                                        borderRadius: '6px',
                                                        border: '1px solid #E5E7EB',
                                                    }}
                                                >
                                                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                                    <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                                        {formatFileName(file.name || file)}
                                                    </Typography>
                                                    <IconButton
                                                        onClick={() => removeOutputFile(index)}
                                                        sx={{ padding: '2px' }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Add training set button - positioned below the upload card */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                                
                                    {outputFileList.length > 0 && (
                                        <Button
                                            onClick={onDiscardTrainingSetClick}
                                            startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px' }} />}
                                            sx={{
                                                color: '#DC2626',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: '1px solid #F3F4F6',
                                                backgroundColor: '#FFFFFF',
                                                '&:hover': {
                                                    backgroundColor: '#FEF2F2',
                                                    borderColor: '#FECACA',
                                                },
                                            }}
                                        >
                                            Remove Example File
                                        </Button>
                                    )}
                                    <Button
                                        onClick={onAddTrainingSetClick}
                                        startIcon={<AddIcon sx={{ fontSize: '16px' }} />}
                                        disabled={outputFileList.length === 0}
                                        sx={{
                                            color: outputFileList.length === 0 ? '#9CA3AF' : '#4F46E5',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            border: `1px solid ${outputFileList.length === 0 ? '#E5E7EB' : '#E5E7EB'}`,
                                            backgroundColor: '#FFFFFF',
                                            cursor: outputFileList.length === 0 ? 'not-allowed' : 'pointer',
                                            '&:hover': {
                                                backgroundColor: outputFileList.length === 0 ? '#FFFFFF' : '#F8FAFC',
                                                borderColor: outputFileList.length === 0 ? '#E5E7EB' : '#4F46E5',
                                            },
                                            '&:disabled': {
                                                color: '#9CA3AF',
                                                backgroundColor: '#FFFFFF',
                                                borderColor: '#E5E7EB',
                                            },
                                        }}
                                    >
                                        Add Example File
                                    </Button>
                                
                            </Box>
                        </CardContent>
                    </Card>

                    {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={onAddTrainingSetClick}
                            startIcon={<AddIcon sx={{ fontSize: '16px' }} />}
                            disabled={outputFileList.length === 0}
                            sx={{
                                color: outputFileList.length === 0 ? '#9CA3AF' : '#4F46E5',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: `1px solid ${outputFileList.length === 0 ? '#E5E7EB' : '#E5E7EB'}`,
                                backgroundColor: '#FFFFFF',
                                cursor: outputFileList.length === 0 ? 'not-allowed' : 'pointer',
                                '&:hover': {
                                    backgroundColor: outputFileList.length === 0 ? '#FFFFFF' : '#F8FAFC',
                                    borderColor: outputFileList.length === 0 ? '#E5E7EB' : '#4F46E5',
                                },
                                '&:disabled': {
                                    color: '#9CA3AF',
                                    backgroundColor: '#FFFFFF',
                                    borderColor: '#E5E7EB',
                                },
                            }}
                        >
                            Add training set
                        </Button>
                    </Box> */}

                    {/* Training Sets Display */}
                    {trainingSetFiles.length > 0 ? (
                        <Box sx={{ mb: "110px" }}>
                            {trainingSetFiles.map((trainingSet, index) => (
                                <TrainingSetCard
                                    key={trainingSet.id}
                                    trainingSet={trainingSet}
                                    index={index}
                                    onDelete={handleDeleteTrainingSet}
                                    onViewFile={handleViewFile}
                                    onReplaceFile={handleReplaceFile}
                                />
                            ))}
                        </Box>
                    ) : (
                        // Empty state can be added here if needed
                        null
                    )}
                </Box>
            </Box>

            {/* <AsynchronousContinueButton
                onClick={handleContinueClick}
                text="Continue"
                loading={loading}
                setLoading={setLoading} 
                disabled={loading}
            /> */}

            <ContinueButton
                onClick={handleContinueClick}
                text="Continue"
            />

            {/* Document Viewer Modal */}
            <DocumentViewerModal
                open={documentViewerOpen}
                onClose={handleCloseViewer}
                fileUrl={selectedDocument?.url || selectedDocument?.name}
                fileName={formatFileName(selectedDocument?.name || selectedDocument)}
            />

            {/* Confirmation Dialog */}
            <ContinueWithoutTrainingSetsDialog
                open={confirmDialogOpen}
                onClose={handleCancelConfirm}
                onConfirm={handleConfirmContinue}
            />
        </Box>
    )
}